import { AxiosResponse } from "axios";
import dayjs from "dayjs";
import { IValidationResult } from "lobbie";
import { useEffect } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import FullScreenLoadingSpinner from "src/components/shared/loading/FullScreenLoadingSpinner";
import SchedulingBrandingLogo from "src/components/shared/scheduling/cards/SchedulingBrandingLogo";
import {
    BASE_PUBLIC_API_ROUTE,
    DEFAULT_ERROR_MESSAGE,
    IS_MOBILE_PHONE,
    ONE_MINUTE_IN_MS,
    ROUTES,
} from "src/constants";
import {
    useLobbieAccountPublicUuid,
    useLobbieSelfSchedulingSettingPublicUuid,
} from "src/hooks/accounts/accounts";
import { usePatientSelfSchedulingContext } from "src/hooks/patient_scheduling/usePatientSelfSchedulingContext";
import { useAxiosPublicPost } from "src/hooks/useAxios";
import { useLogoutNoRedirect } from "src/hooks/user";
import { handleError, isFailedRequest, logDev, notify, zeroTime } from "src/utils";
import { ISelfScheduleSettings } from "../../../patients/scheduling/types";
import PatientSchedulingAppointmentType from "./dep/PatientSchedulingAppointmentType";
import PatientSchedulingBookingConfirmed from "./dep/PatientSchedulingBookingConfirmed";
import PatientSchedulingConfirmation from "./dep/PatientSchedulingConfirmation";
import PatientSchedulingFooter from "./dep/PatientSchedulingFooter";
import PatientSchedulingLocation from "./dep/PatientSchedulingLocation";
import PatientSchedulingPickAppointmentDateAndTime from "./dep/PatientSchedulingPickAppointmentDateAndTime";
import PatientSchedulingPractitioner from "./dep/PatientSchedulingPractitioner";
import PatientSchedulingSideBar from "./dep/PatientSchedulingSideBar";
import PatientSchedulingSMSConfirmation from "./dep/PatientSchedulingSMSConfirmation";
import PatientSchedulingUserEmail from "./dep/PatientSchedulingUserEmail";
import PatientSchedulingUserInfo from "./dep/PatientSchedulingUserInfo";

const PatientSchedulingHome = () => {
    const poster = useAxiosPublicPost();
    const logout = useLogoutNoRedirect(true);
    const selfSchedulingSettingPublicUuid = useLobbieSelfSchedulingSettingPublicUuid();
    const accountPublicUuid = useLobbieAccountPublicUuid();
    //When the initial page loads recaptcha may not be loaded so we import and
    //Add to the useEffect hook
    const { executeRecaptcha } = useGoogleReCaptcha();

    const {
        selfScheduleSettings,
        setSelfScheduleSettings,
        selfSchedule,
        setSelfSchedule,
        route,
        setRoute,
        getMonth,
        availability,
    } = usePatientSelfSchedulingContext();

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const ctuValue = queryParams.get("ctu");
        if (ctuValue) {
            setSelfSchedule((prevSelfSchedule: any) => {
                return {
                    ...prevSelfSchedule,
                    conversionTrackingUuid: ctuValue,
                };
            });
        }
    }, []);
    useEffect(() => {
        logout()
            .then(() => {
                logDev("Logged out");
            })
            .catch((e) => {
                logDev("Error logging out", e);
            });

        poster(`${BASE_PUBLIC_API_ROUTE}/patientscheduling/create`, {
            accountPublicUuid,
            selfSchedulingSettingPublicUuid,
        })
            .then((response: AxiosResponse | void) => {
                const result =
                    response && (response.data as ISelfScheduleSettings | IValidationResult);
                if (result && isFailedRequest(result)) {
                    notify({
                        level: "error",
                        title: "Could not start scheduling.",
                        message: (result as IValidationResult).message || DEFAULT_ERROR_MESSAGE,
                        duration: ONE_MINUTE_IN_MS,
                    });
                } else if (result) {
                    const currSelfScheduleSettings = result as ISelfScheduleSettings;
                    setSelfScheduleSettings(currSelfScheduleSettings);
                    //Set to the default appointment type if not selectable
                    setSelfSchedule((prevSelfSchedule: any) => {
                        return {
                            ...prevSelfSchedule,
                            appointmentType: currSelfScheduleSettings.appointmentType,
                            location: currSelfScheduleSettings.location,
                            practitionerId: null,
                            date: null,
                            time: null,
                            isNotifyEarlierAppointment: false,
                            email: null,
                            phone: null,
                            accountPatient: null,
                            isEmailConfirmed: false,
                            isPhoneConfirmed: false,
                            selectedAccountPatient: null,
                            appointments: null,
                        };
                    });

                    setRoute(getInitialRoute(currSelfScheduleSettings));
                }
            })
            .catch(handleError);
        // NOTE: this will endlessly loop to create a new scheduling session if all the dependencies are included
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selfSchedulingSettingPublicUuid, accountPublicUuid, executeRecaptcha]);

    useEffect(() => {
        if (selfSchedule && selfSchedule.location && selfSchedule.appointmentType) {
            const now = dayjs();
            const startDate = zeroTime(now)
                .set("month", availability.selectedMonth)
                .set("year", availability.selectedYear);

            const startUnix = zeroTime(startDate).set("date", 1).valueOf(); // beginning of month
            const endDate = startDate.set("date", startDate.daysInMonth()).add(1, "day");
            const endUnix = endDate.valueOf();
            getMonth(startUnix, endUnix);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        selfSchedule?.location,
        selfSchedule?.appointmentType,
        availability.selectedMonth,
        availability.selectedYear,
        getMonth,
    ]);

    const getInitialRoute = (result: ISelfScheduleSettings) => {
        if (result.appointmentTypeSelectable) {
            return ROUTES.patientSchedulingApptType;
        }
        if (result.locationSelectable) {
            return ROUTES.patientSchedulingLocation;
        }
        if (result.practitionerSelectable) {
            return ROUTES.patientSchedulingPractitioner;
        }
        return ROUTES.patientSchedulingCalendar;
    };

    const patientSelfSchedulingContext = usePatientSelfSchedulingContext();

    if (
        patientSelfSchedulingContext === undefined ||
        !route ||
        !selfSchedule ||
        !selfScheduleSettings
    ) {
        return <FullScreenLoadingSpinner message={"Loading schedule details..."} />;
    }

    const handleSetRoute = (newRoute: string) => {
        setRoute(newRoute);
    };

    return (
        <>
            {/* <Prompt when={scheduling.isDirty} message="Are you sure you want to leave?" /> */}
            <div className="row mt-4">
                {selfScheduleSettings?.branding && (
                    <>
                        <div className="col-3">
                            <SchedulingBrandingLogo branding={selfScheduleSettings?.branding} />
                        </div>
                        <div className="col-9">&nbsp;</div>
                    </>
                )}
            </div>
            <div className="row mt-4">
                {!IS_MOBILE_PHONE && (
                    <div className="col-4">
                        <PatientSchedulingSideBar setRoute={handleSetRoute} />
                    </div>
                )}
                <div
                    className={`col-${IS_MOBILE_PHONE ? 12 : 8} ${
                        IS_MOBILE_PHONE ? "" : "border rounded"
                    }`}
                >
                    {route === ROUTES.patientSchedulingApptType && (
                        <PatientSchedulingAppointmentType />
                    )}
                    {route === ROUTES.patientSchedulingLocation && <PatientSchedulingLocation />}
                    {route === ROUTES.patientSchedulingPractitioner && (
                        <PatientSchedulingPractitioner />
                    )}
                    {route === ROUTES.patientSchedulingCalendar && (
                        <>
                            <PatientSchedulingPickAppointmentDateAndTime />
                            <PatientSchedulingFooter />
                        </>
                    )}
                    {route === ROUTES.patientSchedulingUserEmail && <PatientSchedulingUserEmail />}
                    {route === ROUTES.patientSchedulingSMSConfirmation && (
                        <PatientSchedulingSMSConfirmation />
                    )}
                    {route === ROUTES.patientSchedulingUserInfo && <PatientSchedulingUserInfo />}
                    {route === ROUTES.patientSchedulingConfirmation && (
                        <PatientSchedulingConfirmation />
                    )}
                    {route === ROUTES.patientSchedulingBookingConfirmed && (
                        <PatientSchedulingBookingConfirmed />
                    )}
                </div>
            </div>
        </>
    );
};

export default PatientSchedulingHome;
